import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Package, Edit, Trash2, Plus, LogOut, X, Copy, GripVertical, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import CloudinaryUpload from '@/components/CloudinaryUpload';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Sortable Product Row Component
function SortableProductRow({ product, onEdit, onDuplicate, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: product.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`border-b ${isDragging ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
    >
      <td className="p-4">
        <button {...attributes} {...listeners} className="cursor-move text-gray-400 hover:text-black">
          <GripVertical className="w-5 h-5" />
        </button>
      </td>
      <td className="p-4">
        <div className="flex items-center gap-3">
          {product.images?.[0] && (
            <img src={product.images[0]} alt={product.name} className="w-12 h-12 object-cover border" />
          )}
          <div>
            <p className="font-medium">{product.name}</p>
            <p className="text-sm text-gray-500">{product.sku}</p>
          </div>
        </div>
      </td>
      <td className="p-4">{product.category}</td>
      <td className="p-4">{product.brand}</td>
      <td className="p-4">{product.price.toFixed(2)}€</td>
      <td className="p-4">
        <span className={`px-2 py-1 rounded text-xs ${product.stock > 10 ? 'bg-green-100' : 'bg-red-100'}`}>
          {product.stock}
        </span>
      </td>
      <td className="p-4 text-right">
        <Button
          variant="ghost"
          size="sm"
          onClick={onEdit}
          className="mr-2"
          title="Editar"
        >
          <Edit className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDuplicate}
          className="mr-2 text-blue-600"
          title="Duplicar producto"
        >
          <Copy className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          className="text-red-600"
          title="Eliminar"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </td>
    </tr>
  );
}

export default function AdminProducts({ user, setUser }) {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const navigate = useNavigate();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    // Filter products based on search term
    if (searchTerm.trim() === '') {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(p => 
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.sku && p.sku.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredProducts(filtered);
    }
  }, [searchTerm, products]);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    fetchProducts();
  }, [user, navigate]);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/products`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setProducts(data.products || []);
      setFilteredProducts(data.products || []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!confirm('¿Estás seguro de eliminar este producto?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/products/${productId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Producto eliminado');
        fetchProducts();
      }
    } catch (error) {
      toast.error('Error al eliminar producto');
    }
  };

  const handleDuplicate = async (productId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/products/${productId}/duplicate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Producto duplicado: ${data.name}`);
        fetchProducts();
      }
    } catch (error) {
      toast.error('Error al duplicar producto');
    }
  };

  const handleSave = async (productData) => {
    try {
      const token = localStorage.getItem('token');
      const url = editingProduct 
        ? `${BACKEND_URL}/api/admin/products/${editingProduct.id}`
        : `${BACKEND_URL}/api/admin/products`;
      
      const response = await fetch(url, {
        method: editingProduct ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(productData)
      });
      
      if (response.ok) {
        toast.success(editingProduct ? 'Producto actualizado' : 'Producto creado');
        setShowForm(false);
        setEditingProduct(null);
        fetchProducts();
      }
    } catch (error) {
      toast.error('Error al guardar producto');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = products.findIndex((p) => p.id === active.id);
      const newIndex = products.findIndex((p) => p.id === over.id);

      const newProducts = arrayMove(products, oldIndex, newIndex);
      setProducts(newProducts);

      // Save new order to backend
      await saveProductOrder(newProducts);
    }
  };

  const saveProductOrder = async (orderedProducts) => {
    try {
      const token = localStorage.getItem('token');
      
      const productsWithOrder = orderedProducts.map((p, index) => ({
        id: p.id,
        order: index
      }));

      const response = await fetch(`${BACKEND_URL}/api/admin/products/reorder`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ products: productsWithOrder })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Orden actualizado');
      } else {
        toast.error(`Error: ${data.detail || 'Error al guardar'}`);
      }
    } catch (error) {
      console.error('Reorder exception:', error);
      toast.error('Error de conexión');
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-white flex items-center justify-center">Cargando...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-black text-white">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display' }}>ADMIN - ARTHUR FITZ</h1>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-white hover:underline">Ver Tienda</Link>
            <Button onClick={handleLogout} variant="outline" className="text-white border-white">
              <LogOut className="w-4 h-4 mr-2" /> Cerrar Sesión
            </Button>
          </div>
        </div>
      </header>

      <nav className="bg-white border-b">
        <div className="container mx-auto px-6 py-3">
          <div className="flex gap-6">
            <Link to="/admin" className="text-gray-600 hover:text-black">Dashboard</Link>
            <Link to="/admin/products" className="font-medium text-black border-b-2 border-black pb-2">Productos</Link>
            <Link to="/admin/orders" className="text-gray-600 hover:text-black">Pedidos</Link>
            <Link to="/admin/customers" className="text-gray-600 hover:text-black">Clientes</Link>
            <Link to="/admin/coupons" className="text-gray-600 hover:text-black">Cupones</Link>
            <Link to="/admin/quotes" className="text-gray-600 hover:text-black">Presupuestos</Link>
            <Link to="/admin/shipping" className="text-gray-600 hover:text-black">Envios</Link>
            <Link to="/admin/contact-requests" className="text-gray-600 hover:text-black">Solicitudes</Link>
            <Link to="/admin/notices" className="text-gray-600 hover:text-black">Avisos</Link>
            <Link to="/admin/newsletter" className="text-gray-600 hover:text-black">Newsletter</Link>
            <Link to="/admin/gallery" className="text-gray-600 hover:text-black">Galería</Link>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display' }}>Gestión de Productos</h2>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Buscar productos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64"
              />
            </div>
            <Button onClick={() => { setEditingProduct(null); setShowForm(true); }} className="bg-black text-white">
              <Plus className="w-4 h-4 mr-2" /> Nuevo Producto
            </Button>
          </div>
        </div>

        {searchTerm && (
          <p className="text-sm text-gray-600 mb-4">
            {filteredProducts.length} producto(s) encontrado(s)
          </p>
        )}

        {showForm ? (
          <ProductForm 
            product={editingProduct} 
            onSave={handleSave} 
            onCancel={() => { setShowForm(false); setEditingProduct(null); }}
          />
        ) : (
          <div className="bg-white border-2 border-black">
            <div className="overflow-x-auto">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-black">
                      <th className="w-12"></th>
                      <th className="text-left p-4">Producto</th>
                      <th className="text-left p-4">Categoría</th>
                      <th className="text-left p-4">Marca</th>
                      <th className="text-left p-4">Precio</th>
                      <th className="text-left p-4">Stock</th>
                      <th className="text-right p-4">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    <SortableContext
                      items={filteredProducts.map(p => p.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {filteredProducts.map((product) => (
                        <SortableProductRow
                          key={product.id}
                          product={product}
                          onEdit={() => { setEditingProduct(product); setShowForm(true); }}
                          onDuplicate={() => handleDuplicate(product.id)}
                          onDelete={() => handleDelete(product.id)}
                        />
                      ))}
                    </SortableContext>
                  </tbody>
                </table>
              </DndContext>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ProductForm({ product, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    name: product?.name || '',
    description: product?.description || '',
    price: product?.price || 0,
    stock: product?.stock || 0,
    category: product?.category || 'protesis',
    subcategory: product?.subcategory || '',
    brand: product?.brand || 'Arthur Fitz',
    sku: product?.sku || '',
    images: product?.images || [],
    has_variants: product?.has_variants || false,
    variant_options: product?.variant_options || {},
    allow_backorder: product?.allow_backorder || false,
    availability_date: product?.availability_date || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...product, ...formData });
  };

  return (
    <div className="bg-white p-6 border-2 border-black mb-6">
      <h3 className="text-xl font-bold mb-4">{product ? 'Editar Producto' : 'Nuevo Producto'}</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Nombre *</Label>
            <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
          </div>
          <div>
            <Label>SKU</Label>
            <Input value={formData.sku} onChange={(e) => setFormData({...formData, sku: e.target.value})} />
          </div>
        </div>
        
        <div>
          <Label>Descripción</Label>
          <textarea 
            className="w-full border p-2 rounded" 
            rows="3"
            value={formData.description} 
            onChange={(e) => setFormData({...formData, description: e.target.value})}
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label>Precio (€) *</Label>
            <Input type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value)})} required />
          </div>
          <div>
            <Label>Stock *</Label>
            <Input type="number" value={formData.stock} onChange={(e) => setFormData({...formData, stock: parseInt(e.target.value)})} required />
          </div>
          <div>
            <Label>Marca</Label>
            <Input value={formData.brand} onChange={(e) => setFormData({...formData, brand: e.target.value})} />
          </div>
        </div>

        <div className="border-2 border-orange-300 bg-orange-50 p-4 rounded space-y-3" data-testid="backorder-section">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="allow_backorder"
              checked={formData.allow_backorder}
              onChange={(e) => setFormData({...formData, allow_backorder: e.target.checked})}
              data-testid="allow-backorder-checkbox"
            />
            <label htmlFor="allow_backorder" className="font-semibold text-orange-800 cursor-pointer">
              📦 Aceptar pedidos aunque esté agotado (pre-venta)
            </label>
          </div>
          <p className="text-xs text-orange-700">
            Los clientes podrán comprar aunque el stock sea 0. Se les avisará que el producto se enviará en la fecha indicada abajo.
          </p>
          {formData.allow_backorder && (
            <div>
              <Label className="text-orange-900">Fecha de disponibilidad *</Label>
              <Input
                type="date"
                value={formData.availability_date}
                onChange={(e) => setFormData({...formData, availability_date: e.target.value})}
                required={formData.allow_backorder}
                data-testid="availability-date-input"
                className="max-w-xs"
              />
              <p className="text-xs text-orange-700 mt-1">
                Fecha estimada de envío. Se mostrará al cliente en la ficha, carrito y email de pedido.
              </p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Categoría</Label>
            <select className="w-full border p-2 rounded" value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})}>
              <option value="protesis">Prótesis Capilares</option>
              <option value="mantenimiento">Productos de Mantenimiento</option>
            </select>
          </div>
          <div>
            <Label>Subcategoría</Label>
            {formData.category === 'mantenimiento' ? (
              <select
                className="w-full border p-2 rounded"
                value={formData.subcategory}
                onChange={(e) => setFormData({...formData, subcategory: e.target.value})}
                data-testid="product-subcategory-select"
              >
                <option value="">— Sin subcategoría —</option>
                <option value="disolventes">1. Disolventes</option>
                <option value="adhesivos_liquidos">2. Adhesivos Líquidos o en Crema</option>
                <option value="adhesivos_doble_cara">3. Adhesivos de Doble Cara</option>
                <option value="otros_mantenimiento">4. Otros Productos de Mantenimiento</option>
              </select>
            ) : (
              <select
                className="w-full border p-2 rounded"
                value={formData.subcategory}
                onChange={(e) => setFormData({...formData, subcategory: e.target.value})}
                data-testid="product-subcategory-select"
              >
                <option value="">— Sin subcategoría —</option>
                <option value="malla_francesa">Malla Francesa</option>
                <option value="malla_suiza">Malla Suiza</option>
                <option value="mixta">Mixta</option>
                <option value="Skin">Skin</option>
              </select>
            )}
          </div>
        </div>

        {/* Variant Options Editor */}
        <div className="border-2 border-gray-200 p-4 rounded">
          <div className="flex items-center justify-between mb-3">
            <Label className="text-lg font-bold">Opciones de Variantes</Label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.has_variants}
                onChange={(e) => setFormData({...formData, has_variants: e.target.checked})}
                className="w-4 h-4"
              />
              <span className="text-sm">Producto con variantes</span>
            </label>
          </div>

          {formData.has_variants && (
            <div className="space-y-3 bg-gray-50 p-3 rounded">
              <p className="text-xs text-gray-600 mb-2">
                Gestiona las opciones de variantes (tamaño, color, formato, etc.)
              </p>
              
              {Object.keys(formData.variant_options).map((key) => (
                <div key={key} className="bg-white p-3 border rounded">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="font-medium capitalize">{key}</Label>
                    <button
                      type="button"
                      onClick={() => {
                        const newOptions = {...formData.variant_options};
                        delete newOptions[key];
                        setFormData({...formData, variant_options: newOptions});
                      }}
                      className="text-red-600 text-xs hover:underline"
                    >
                      Eliminar
                    </button>
                  </div>
                  <Input
                    placeholder="Opciones separadas por comas: Opción1, Opción2, Opción3"
                    defaultValue={formData.variant_options[key].join(', ')}
                    onBlur={(e) => {
                      const options = e.target.value.split(',').map(o => o.trim()).filter(o => o);
                      setFormData({
                        ...formData,
                        variant_options: {...formData.variant_options, [key]: options}
                      });
                    }}
                    className="text-sm"
                  />
                </div>
              ))}

              <button
                type="button"
                onClick={() => {
                  const newKey = prompt('Nombre de la nueva opción (ej: size, color, format):');
                  if (newKey && newKey.trim()) {
                    setFormData({
                      ...formData,
                      variant_options: {
                        ...formData.variant_options,
                        [newKey.trim()]: []
                      }
                    });
                  }
                }}
                className="text-sm text-blue-600 hover:underline"
              >
                + Añadir nueva opción de variante
              </button>
            </div>
          )}
        </div>

        <div>
          <Label>Gestión de Imágenes</Label>
          
          {/* Cloudinary Upload */}
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">Subir nueva imagen:</p>
            <CloudinaryUpload 
              onUploadSuccess={(url, publicId) => {
                setFormData({
                  ...formData, 
                  images: [...formData.images, url]
                });
              }}
            />
          </div>

          {/* Manual URL Input */}
          <div>
            <Label className="text-sm">O añadir URLs manualmente (una por línea):</Label>
            <textarea 
              className="w-full border-2 border-gray-300 p-3 rounded focus:border-black mt-2" 
              rows="4"
              placeholder="https://ejemplo.com/imagen1.jpg&#10;https://ejemplo.com/imagen2.jpg&#10;https://ejemplo.com/imagen3.jpg"
              value={formData.images.join('\n')} 
              onChange={(e) => {
                const urls = e.target.value.split('\n').map(url => url.trim()).filter(url => url);
                setFormData({...formData, images: urls});
              }}
              onBlur={(e) => {
                // Clean up on blur
                const urls = e.target.value.split('\n').map(url => url.trim()).filter(url => url);
                setFormData({...formData, images: urls});
              }}
            />
            <p className="text-xs text-gray-500 mt-1">
              Escribe cada URL en una línea diferente. Presiona Enter para nueva línea.
            </p>
          </div>

          {/* Image Previews */}
          {formData.images.length > 0 && (
            <div className="mt-3">
              <p className="text-sm font-medium mb-2">Imágenes actuales ({formData.images.length}):</p>
              <div className="flex gap-2 flex-wrap">
                {formData.images.map((img, idx) => (
                  <div key={img || `img-${idx}`} className="relative group">
                    <img 
                      src={img} 
                      alt={`Preview ${idx + 1}`} 
                      className="w-24 h-24 object-cover border-2 border-gray-300 rounded" 
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newImages = formData.images.filter((_, i) => i !== idx);
                        setFormData({...formData, images: newImages});
                      }}
                      className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <Button type="submit" className="bg-black text-white">Guardar</Button>
          <Button type="button" variant="outline" onClick={onCancel}>Cancelar</Button>
        </div>
      </form>
    </div>
  );
}
